import { Header, ScopeRequirementAll, SCOPES } from 'components'
import { Box, Stack, Typography } from '@mui/material'
import { observeRoles, useCurrentUser } from 'hooks'
import { listGroups } from 'pages/admin/common/adminDatastore'
import { defaultRoutes } from 'pages/AllRoutes'
import { useMemo, useState } from 'react'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { RolesTable } from './components/RolesTable'
import { RoleForm } from './components/RoleForm'
import { LoadingButton } from '@mui/lab'
import { useQuery } from '@tanstack/react-query'

export const RolesPage = () => {
  const { currentUser } = useCurrentUser()
  const routes = useMemo(() => {
    return defaultRoutes(currentUser.isAdmin)
  }, [currentUser.isAdmin])
  const groups = currentUser.getGroups()
  const hasCognitoAdmin = groups.filter((g) => g.toLowerCase() === 'administrators').length > 0
  const [selectedRole, setSelectedRole] = useState()
  const { database } = useCurrentDeckContext()
  const [rolesLoading, roles] = observeRoles()
  const sitesAvailable = currentUser.getSitesForScope(SCOPES.roleManagement.Write)
  const cognitoQuery = useQuery({
    queryKey: ['cognitoQuery'],
    queryFn: async () => {
      const data = await listGroups()
      return data.Groups.sort((groupA, groupB) => {
        return groupA.GroupName.localeCompare(groupB.GroupName)
      })
    },
    refetchIntervalInBackground: false,
    refetchOnReconnect: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })

  const [openRoleModal, setOpenRoleModal] = useState(false)

  const writingSitesAvailable = currentUser.getSitesForScope(SCOPES.roleManagement.Write)

  const rows = useMemo(() => {
    const filteredRoles = currentUser.isAdmin
      ? roles
      : roles.filter((r) => r.SiteIds?.some((s) => sitesAvailable.includes(s)))
    return filteredRoles.map((item) => {
      return {
        id: item?.id,
        Name: item?.Name,
        CustomerId: item.CustomerId,
        Customer: database.customers?.find((c) => c.id === item.CustomerId)?.Name ?? '',
        SiteIds: item.SiteIds,
        Sites:
          database.sites
            ?.filter((s) => item.SiteIds?.includes(s.id))
            .map((s) => s.Name)
            .join(', ') ?? '',
        Description: item?.Description,
        Group: item.Group,
        Scopes: item?.Scopes,
        Enabled: item?.Enabled
      }
    })
  }, [currentUser.isAdmin, database.customers, roles])

  return (
    <Box sx={{ width: '100%', height: '100%', px: '15px' }}>
      <Header routes={routes} />

      <Stack sx={{ pt: 2 }} direction="row" alignItems="center">
        <Typography variant="h1">Manage roles</Typography>

        {!hasCognitoAdmin && currentUser.hasAllScopes([SCOPES.roleManagement.Write]) && (
          <Typography color="error">
            Your role has User write access, but does not have Admin Cognito permission. Adding/editing roles will be
            disabled.
          </Typography>
        )}
        <ScopeRequirementAll requirements={[SCOPES.roleManagement.Write]}>
          <LoadingButton
            sx={{
              ml: '1em'
            }}
            component="label"
            variant="contained"
            size="small"
            loading={cognitoQuery.isFetching}
            color="secondary"
            onClick={() => {
              setSelectedRole(undefined)
              setOpenRoleModal(true)
            }}
            tabIndex={-1}>
            Add role
          </LoadingButton>
        </ScopeRequirementAll>
      </Stack>

      <RolesTable
        setRole={(role) => {
          setSelectedRole(role)
          setOpenRoleModal(true)
        }}
        data={rows}
        isLoading={rolesLoading || cognitoQuery.isLoading}
      />

      {openRoleModal && (
        <RoleForm
          data={selectedRole}
          closeModal={() => setOpenRoleModal(false)}
          availableCognitoGroups={cognitoQuery.data}
          customers={database.customers}
          sites={
            currentUser.isAdmin ? database.sites : database.sites.filter((s) => writingSitesAvailable.includes(s.id))
          }
        />
      )}
    </Box>
  )
}
